/**
 * HIM-4783 — FAQPage Schema.org as a single page-level JSON-LD block.
 *
 * The FAQ blocks used to publish their schema as HTML microdata (itemscope/itemtype/itemprop)
 * stamped onto the rendered accordion, one FAQPage claim per block. SEO Strategy asked for
 * LD+JSON instead, and a page carrying two FAQPage claims is what makes a validator ignore
 * both — so the questions from every FAQ block on the page are collected here and emitted as
 * ONE `application/ld+json` FAQPage, while the blocks themselves render no microdata at all.
 *
 * Two block shapes feed it, because two Storyblok components publish FAQs:
 *   - `FAQ`     — `dtvFaq` and `faqPanel`, both rendered by @directv-digital/dds.ui.dtv-faq
 *   - `DDS_FAQ` — the v3 organism at directv-design-system/organisms/faq
 *
 * All values are reduced to plain text so no markup reaches the JSON.
 */
import { decodeHtmlEntities } from "./productJsonLd";

/** Blok types that carry FAQs. `FAQ` covers both `dtvFaq` and `faqPanel`. */
export const FAQ_V2_TYPE = "FAQ";
export const FAQ_V3_TYPE = "DDS_FAQ";

export interface FaqQuestion {
  question: string;
  answer: string;
}

/**
 * Tags that end a line of reading. Everything else is inline.
 *
 * ⚠ NOT productJsonLd's stripHtmlTags, which replaces EVERY tag with a space. A product name
 * is a phrase and survives that; an answer is prose, and `See <a href="…">this page</a>.`
 * comes out as "See this page ." — a space before the full stop, in every answer an author
 * linked or bolded. So inline tags close up and only a block boundary becomes a space.
 */
const BLOCK_BOUNDARY_TAGS =
  /<\/?(?:p|div|br|li|ul|ol|table|tr|td|th|h[1-6]|section|article|header|footer|blockquote)\b[^>]*>/gi;

/**
 * An answer or question as the reader hears it: entities decoded, markup gone, whitespace
 * collapsed. Entities are decoded FIRST so entity-encoded markup (`&lt;b&gt;`) is stripped
 * too, which is the same order — and the same reason — as the Product builder.
 */
export const faqPlainText = (value?: string | null): string => decodeHtmlEntities(value ?? "")
  .replace(BLOCK_BOUNDARY_TAGS, " ")
  .replace(/<[^>]*>/g, "")
  .replace(/\s+/g, " ")
  .trim();

const asArray = (value: unknown): any[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value === null || value === undefined ? [] : [value];
};

/**
 * The effective blok type, unwrapping OVERWRITER the same way both canvases do — an
 * overwritten FAQ is still an FAQ, and reading `type` alone would skip it.
 *
 * `props.type` is the last fallback because commonSbFixer only lifts `type` to the top level
 * for blok types registered in ComponentNames, and the v3 canvas reads both.
 */
export const resolveFaqBlokType = (block: any): string => {
  const type = block?.type === "OVERWRITER" ?
    block?.props?.component :
    block?.type || block?.props?.type;
  return type || "";
};

/** The props a blok actually renders with; OVERWRITER holds them one level down. */
export const resolveFaqBlokProps = (block: any): any => {
  if (block?.type !== "OVERWRITER") {
    return block?.props;
  }
  const objProps = block?.props?.objProps;
  return Array.isArray(objProps) ? objProps?.[0] : objProps;
};

/**
 * Is this block's FAQPage switch off?
 *
 * ⚠ THE TWO COMPONENTS READ THEIR SWITCH DIFFERENTLY, AND THAT IS PRESERVED ON PURPOSE.
 * dtv-faq tests `!props?.disableSchemaOrg` — plain truthiness, so the string "false" a
 * Storyblok switch can publish turns it OFF. The v3 organism tests `value === true` for
 * exactly that reason. Normalising the two here would silently add a FAQPage claim to every
 * v2 page whose switch publishes as "false", which is a content decision, not a refactor.
 * A page that shows schema today shows schema after this change, and no other page does.
 */
export const isFaqSchemaDisabled = (props: any, type: string): boolean => (type === FAQ_V3_TYPE ? props?.disable_schema_org === true : Boolean(props?.disableSchemaOrg));

/**
 * The answer as the reader sees it. The v2 accordion draws the main copy, then an optional
 * list (with its own optional sub-lists), then an optional closing line — all three carry
 * `itemProp="text"` today, so dropping the last two would publish a shorter answer than the
 * page displays.
 */
const v2AnswerText = (accordionContent: any): string => {
  const parts: string[] = [accordionContent?.content];

  asArray(accordionContent?.listItems).forEach(item => {
    parts.push(item?.content);
    asArray(item?.subListItems).forEach(subItem => parts.push(subItem?.content));
  });

  parts.push(accordionContent?.bottomContent?.content);

  return faqPlainText(parts.filter(Boolean).join(" "));
};

/** Questions from a `FAQ` blok — `dtvFaq` / `faqPanel`. */
const v2Questions = (props: any): FaqQuestion[] => asArray(props?.faq?.panels).map(panel => ({
  question: faqPlainText(panel?.accordionHeading?.content),
  answer: v2AnswerText(panel?.accordionContent)
}));

/**
 * Questions from a `DDS_FAQ` blok. `answer_content` is authored as a Textarea, so it arrives
 * as an HTML string; a Richtext document object has no text to strip and is dropped by the
 * eligibility filter below — the same row the organism itself refuses to render.
 */
const v3Questions = (props: any): FaqQuestion[] => asArray(props?.items).map(item => ({
  question: faqPlainText(item?.question_content),
  answer: typeof item?.answer_content === "string" ? faqPlainText(item.answer_content) : ""
}));

/**
 * Every FAQ on the page, in document order, from every FAQ block that has its schema
 * switch on.
 *
 * A question needs both halves: a `Question` with no `name`, or an `acceptedAnswer` with no
 * `text`, is a structural error in the Rich Results Test rather than a thinner result.
 * Repeated questions are dropped for the same reason — one page, one entry per question.
 */
export const collectFaqQuestions = (blocks: unknown): FaqQuestion[] => {
  const seen = new Set<string>();

  return (Array.isArray(blocks) ? blocks : []).reduce((questions: FaqQuestion[], block) => {
    const type = resolveFaqBlokType(block);
    if (type !== FAQ_V2_TYPE && type !== FAQ_V3_TYPE) {
      return questions;
    }

    const props = resolveFaqBlokProps(block);
    if (isFaqSchemaDisabled(props, type)) {
      return questions;
    }

    (type === FAQ_V2_TYPE ? v2Questions(props) : v3Questions(props)).forEach(entry => {
      const key = entry.question.toLowerCase();
      if (!entry.question || !entry.answer || seen.has(key)) {
        return;
      }
      seen.add(key);
      questions.push(entry);
    });

    return questions;
  }, []);
};

/**
 * Build the page's schema.org FAQPage object, or null when the page has no eligible FAQ —
 * an empty `mainEntity` is an error rather than an empty result, so the page emits nothing.
 */
export const buildFaqPageJsonLd = (blocks: unknown): Record<string, any> | null => {
  const questions = collectFaqQuestions(blocks);
  if (questions.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map(entry => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: entry.answer
      }
    }))
  };
};

/**
 * Serialize for `dangerouslySetInnerHTML`. `<` is escaped to its unicode form so a stray `<`
 * in CMS copy can never break out of the `<script>` tag. Returns "" when there is nothing to
 * emit. Mirrors buildProductJsonLdString (HIM-4785).
 */
export const buildFaqPageJsonLdString = (blocks: unknown): string => {
  const faqPage = buildFaqPageJsonLd(blocks);
  return faqPage ? JSON.stringify(faqPage).replace(/</g, "\\u003c") : "";
};

/**
 * The props an FAQ block renders with, with its own microdata switched off.
 *
 * The page-level block above is the only FAQPage claim now, so the accordion must stop
 * stamping `itemscope`/`itemtype`/`itemprop` onto its markup — two claims on one page is the
 * duplicate-structured-data case the ticket calls out. Non-FAQ blocks pass through untouched.
 */
export const withFaqMicrodataDisabled = (type: string, props: any): any => {
  if (type === FAQ_V2_TYPE) {
    return {
      ...props,
      disableSchemaOrg: true
    };
  }
  if (type === FAQ_V3_TYPE) {
    return {
      ...props,
      disable_schema_org: true
    };
  }
  return props;
};
